<template>

    <div v-if="form.modificationsToDesign == 'Yes'"
        class="shadow-md divide-y-2 my-2 border border-blue-200 rounded-lg bg-blue-50 px-4 py-5 sm:px-6 "
        v-for="(reorderJobs, index) in form.reorderJobs" :key="index">
        <div class="w-full flex items-center justify-between">
            <p class="text-xl font-semibold my-4 text-blue-600 px-3 ">Reorder Job {{ index + 1 }}</p>
            <div class="">
                <button type="button" @click.prevent="removeReorderJob"
                    class="-m-2.5 flex   p-2.5 text-blue-600 hover:text-blue-800">
                    <span class="sr-only">Remove</span>
                    <TrashIcon class="h-5 w-5" aria-hidden="true" />
                </button>
            </div>
        </div>

        <div class="w-full">
            <BaseInput requires="True" label="PO Number / PD Number / Job Number" :name="`reorderJobs[${index}][jobNumber]`"
                placeholder="Please enter your number" v-model="reorderJobs.jobNumber" />

            <BaseTextArea label="Part Name or Part Description" :name="`reorderJobs[${index}][description]`"
                placeholder="Enter the Part Name or Part Description from your previous order, if you don't have the Job Number or PO Number."
                v-model="reorderJobs.partNameDesc" :rows="4" />

            <div class="grid lg:space-x-8 items-center justify-between lg:grid-cols-2">
                <div class="mb-1">
                    <BaseInput requires="True" label="Quantity" :name="`reorderJobs[${index}][Quantity]`" type="number"
                        min="1" max="100" placeholder="Please enter your number" v-model="reorderJobs.Quantity" />
                </div>
                <div class="">
                    <KeepAlive>
                        <FileComponent @uploaded="handleFileUpload(index)" label="Upload your file" />
                    </KeepAlive>

                </div>
            </div>

            <Alert class="bg-blue-200 my-1 text-xs text-zinc-500"
                message="sldasm (Solidworks assembly), .step (STEP file), .stp (STEP file), .igs (IGES file), .asm (ProE 3D assembly), .x_t (Unigraphics), .sat (AutoCad 3D assembly), .dwg (AutoCad 2D assembly), .dxf (AutoCad 2D assembly), .stl (3D Printing)" />


            <BaseTextArea requires="True" label="Describe the modifications you require to your previous order."
                v-model="reorderJobs.Description" />
        </div>
    </div>

    <!-- modal to show missing fields -->
    <!-- Show modal popup when fields are missing -->
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-10" @close="open = false">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == true"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                    <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />

                                </button>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == false"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                    <XMarkIcon class="h-6 w-6 text-red-600" aria-hidden="true" />

                                </button>

                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="p">
                                        <!-- add missing fields -->
                                        <template v-if="success == false">
                                            <p class="font-semibold">
                                                Please fill out the following fields:

                                            </p>
                                            <p v-for="field in missingFields" :key="field"
                                                class="text-xs items-center text-left leading-6 text-red-900">
                                                * {{ field }}
                                            </p>
                                        </template>
                                        <!-- add success message -->
                                        <template v-if="success == true">
                                            <p>
                                                Thank you for your submission. We will be in touch shortly.
                                            </p>
                                        </template>
                                    </DialogTitle>

                                </div>
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <div class="flex my-6">
        <Button label="Add Another Reorder Job" @click.prevent="addReorderJob" class="float h-10 " />
    </div>

    <div class="flex justify-between my-6">
        <Button label="Back" disabled class="disabled:bg-gray-400" />
        <!-- check missing fields on clicking Next button -->
        <Button label="Next" @click.prevent="checkMissingField" type="submit" />
    </div>
</template>

<script setup>
import { ref, inject, reactive } from 'vue'
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'

import Button from '../formComponents/Button.vue'
import BaseInput from '../formComponents/BaseInput.vue';
import BaseTextArea from '../formComponents/BaseTextArea.vue';
import { TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import FileComponent from '../formComponents/FileComponent.vue';
import Alert from '../formComponents/Alert.vue';


const form = inject('form');
// add reorder job
const addReorderJob = () => {
    form.reorderJobs.push({
        jobNumber: '',
        partNameDesc: '',
        Quantity: '',
        file: null,
        fileName: '',
        Description: '',
    })
}

// remove reorder job
const removeReorderJob = (index) => {
    form.reorderJobs.splice(index, 1);
}

// handle file upload as per index of reorder job
const handleFileUpload = (index) => {
    form.reorderJobs[index].file = form.file;
    // filename
    form.reorderJobs[index].fileName = form.fileName;
}

// store each reorder job data to form object
const reorderJobs = reactive({
    jobNumber: '',
    partNameDesc: '',
    Quantity: '',
    file: null,
    fileName: '',
    Description: '',
})

// check missing fields using watch property of vue js 3 
const missingFields = ref([]);
const success = ref(false);
const open = ref(false);

// check missing fields on clicking next button
const checkMissingField = () => {
    missingFields.value = [];
    form.reorderJobs.forEach((reorderJob, index) => {
        if (!reorderJob.jobNumber) {
            missingFields.value.push(`Reorder Job ${index + 1} PO Number / PD Number / Job Number`);
        }
        if (!reorderJob.partNameDesc) {
            missingFields.value.push(`Reorder Job ${index + 1} Part Name or Part Description`);
        }
        if (!reorderJob.Quantity) {
            missingFields.value.push(`Reorder Job ${index + 1} Quantity`);
        }
        if (!reorderJob.Description) {
            missingFields.value.push(`Reorder Job ${index + 1} Describe the modifications you require to your previous order.`);
        }
    })

    if (missingFields.value.length == 0) {

        form.step = 'projectTimeline';
    } else {
        success.value = false;
        open.value = true;
    }
}

// emit checkMissingField event to parent component
const emitCheckMissingField = () => {
    $emit('checkMissingField');
}








</script>
